import type { TaskWithState } from '../../shared/types'
import { approvalLabel, claimState, isLockedForKid, nextTick, tickState } from '../../shared/domain/approval'
import { Check, Empty } from './ui'

export function TaskList({
  tasks,
  onToggle,
  onApproveClaim,
  byParent,
  viewerId = null,
  showClaimant = false,
  emptyTitle = 'Nothing on this list',
}: {
  tasks: TaskWithState[]
  onToggle: (task: TaskWithState, next: boolean) => void
  /** A parent tapped a shared job someone else took and is waiting on approval. */
  onApproveClaim?: (task: TaskWithState) => void
  /** Whoever is tapping is a parent, so a waiting job's tap approves it. */
  byParent: boolean
  /** Who is looking, so their own approvals read "Approved by you". */
  viewerId?: string | null
  /** Shared household jobs: whoever takes one first owns it for the period. */
  showClaimant?: boolean
  emptyTitle?: string
}) {
  if (tasks.length === 0) return <Empty title={emptyTitle} />

  return (
    <div className="list">
      {tasks.map((task) => {
        const state = tickState(task)
        // On shared jobs someone else took, the tap either approves their tick
        // (a parent) or does nothing (everyone else).
        const claim = showClaimant ? claimState(task, byParent && onApproveClaim !== undefined) : 'free'
        const locked = isLockedForKid(task, byParent) || claim === 'taken'
        const claimant = task.claimedBy
        return (
          <button
            key={task.id}
            type="button"
            className={`check${state === 'done' ? ' check-done' : state === 'waiting' ? ' check-waiting' : ''}${claim === 'taken' ? ' check-claimed' : claim === 'approvable' ? ' check-waiting' : ''}`}
            aria-pressed={task.done}
            disabled={locked}
            onClick={() =>
              claim === 'approvable' ? onApproveClaim?.(task) : onToggle(task, nextTick(task, byParent))
            }
          >
            <Check done={task.done} />
            <span className="check-body">
              <span className="check-title">{task.title}</span>
              {task.detail ? <span className="check-detail">{task.detail}</span> : null}
              {state === 'waiting' ? (
                <span className="check-waiting-text">
                  {byParent ? 'Waiting for a parent — tap to approve' : 'Waiting for a parent to check'}
                </span>
              ) : null}
              {state === 'done' ? <span className="check-detail">{approvalLabel(task.approvedBy, viewerId)}</span> : null}
              {state === 'open' && task.sentBack ? (
                <span className="check-waiting-text">Sent back — give it another go</span>
              ) : null}
              {claimant && claim === 'approvable' ? (
                <span className="check-waiting-text">
                  {claimant.emoji} {claimant.name} did this — tap to approve
                </span>
              ) : null}
              {claimant && claim === 'taken' ? (
                <span className="check-detail">
                  {claimant.emoji} {claimant.name} {claimant.approved ? 'did this' : 'has this'}
                </span>
              ) : null}
            </span>
            <span className="check-points">{task.points}</span>
          </button>
        )
      })}
    </div>
  )
}
